import React, { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Flame, Gift, Wallet, Sparkles } from "lucide-react";
import { SiteHeader } from "../components/SiteHeader";
import { SiteFooter } from "../components/SiteFooter";
import { CountdownTimer } from "../components/CountdownTimer";
import { WHATSAPP_LINK, getInitialLang, saveLang } from "../i18n";

const copy = {
  en: {
    kicker: "All Offers",
    title: "EVERY BONUS. ONE PLACE.",
    sub: "Stacked rewards on every rupee you play. Claim any offer instantly on WhatsApp.",
    claim: "Claim on WhatsApp",
    o1_tag: "Weekly Loss Cashback",
    o1_desc: "Our biggest offer — up to 10% of your weekly losses credited straight back to your wallet, every Monday. No forms, no chasing. Play worry-free all week.",
    o1_points: ["Credited automatically every week", "No upper limit on cashback", "Applies to all games & matches"],
    o2_tag: "Welcome Bonus",
    o2_desc: "A 7% boost on your very first deposit. Start big, win bigger.",
    o2_ends: "Offer ends in",
    o3_tag: "Every Deposit Bonus",
    o3_desc: "Get an extra 5% bonus on every single top-up. No limits, no catches, forever.",
    o4_tag: "Spin The Wheel",
    o4_desc: "One free spin on the bonus wheel — guaranteed reward, every visitor.",
    terms_title: "Good to know",
    terms: ["All bonuses are activated via WhatsApp within minutes.", "Cashback is calculated on net weekly losses.", "18+ only. Play responsibly."],
  },
  hi: {
    kicker: "सभी ऑफर",
    title: "हर बोनस। एक जगह।",
    sub: "आपके हर रुपये पर स्टैक्ड रिवॉर्ड। कोई भी ऑफर WhatsApp पर तुरंत क्लेम करें।",
    claim: "WhatsApp पर क्लेम करें",
    o1_tag: "वीकली लॉस कैशबैक",
    o1_desc: "हमारा सबसे बड़ा ऑफर — हर सोमवार आपके वीकली लॉस का 10% तक सीधा वॉलेट में वापस। कोई फॉर्म नहीं, कोई झंझट नहीं। पूरे हफ्ते बेफिक्र खेलें।",
    o1_points: ["हर हफ्ते ऑटोमैटिक क्रेडिट", "कैशबैक की कोई अपर लिमिट नहीं", "सभी गेम्स और मैचों पर लागू"],
    o2_tag: "वेलकम बोनस",
    o2_desc: "आपकी पहली डिपॉज़िट पर 7% बूस्ट। बड़ी शुरुआत, बड़ी जीत।",
    o2_ends: "ऑफर खत्म होने में",
    o3_tag: "हर डिपॉज़िट बोनस",
    o3_desc: "हर टॉप-अप पर एक्स्ट्रा 5% बोनस। कोई लिमिट नहीं, कोई शर्त नहीं, हमेशा।",
    o4_tag: "स्पिन द व्हील",
    o4_desc: "बोनस व्हील पर एक फ्री स्पिन — हर विज़िटर को गारंटीड रिवॉर्ड।",
    terms_title: "जानने योग्य बातें",
    terms: ["सभी बोनस WhatsApp पर मिनटों में एक्टिवेट होते हैं।", "कैशबैक नेट वीकली लॉस पर कैलकुलेट होता है।", "केवल 18+। ज़िम्मेदारी से खेलें।"],
  },
};

const Claim = ({ label }) => (
  <a
    href={WHATSAPP_LINK}
    target="_blank"
    rel="noreferrer"
    className="mt-6 inline-flex items-center gap-2 bg-wa px-6 py-3 font-display text-sm font-semibold uppercase tracking-wide text-white transition-transform hover:scale-[0.97]"
    data-testid="offer-claim-btn"
  >
    <MessageCircle size={16} /> {label}
  </a>
);

export default function Offers() {
  const [lang, setLangState] = useState(getInitialLang);
  const setLang = (l) => {
    saveLang(l);
    setLangState(l);
  };
  const t = copy[lang];

  return (
    <div className="min-h-screen bg-obsidian font-body text-white">
      <div className="noise-overlay" />
      <SiteHeader lang={lang} setLang={setLang} />

      <main className="mx-auto max-w-7xl px-5 pb-24 pt-32" data-testid="offers-page">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="font-mono text-[12px] uppercase tracking-[0.35em] text-gold">{t.kicker}</p>
          <h1 className="mt-3 max-w-3xl font-display text-4xl font-bold uppercase leading-none tracking-tighter sm:text-5xl lg:text-6xl">
            {t.title}
          </h1>
          <p className="mt-5 max-w-xl text-white/60">{t.sub}</p>
        </motion.div>

        {/* 10% — highlighted first */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="relative mt-14 overflow-hidden border-2 border-gold bg-surface p-8 gold-glow sm:p-12"
          data-testid="offer-card-10"
        >
          <div className="absolute right-0 top-0 bg-gold px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-black">
            {lang === "hi" ? "★ सबसे बड़ा ऑफर" : "★ Biggest Offer"}
          </div>
          <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
            <div>
              <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-gold">
                <Flame size={15} /> {t.o1_tag}
              </p>
              <p className="mt-5 font-display text-8xl font-bold leading-none gold-text sm:text-9xl">10%</p>
              <p className="mt-6 max-w-md text-white/70">{t.o1_desc}</p>
              <Claim label={t.claim} />
            </div>
            <ul className="space-y-4">
              {t.o1_points.map((p, i) => (
                <li key={i} className="flex items-start gap-3 border border-white/10 bg-black/40 p-5">
                  <Sparkles className="mt-0.5 shrink-0 text-gold" size={17} />
                  <span className="text-sm text-white/80">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="border border-white/10 bg-surface p-8 sm:p-10"
            data-testid="offer-card-7"
          >
            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-white/50">
              <Gift size={15} className="text-gold" /> {t.o2_tag}
            </p>
            <p className="mt-5 font-display text-7xl font-bold leading-none gold-text">7%</p>
            <p className="mt-4 text-sm text-white/70">{t.o2_desc}</p>
            <div className="mt-6 border-t border-white/10 pt-5">
              <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{t.o2_ends}</p>
              <CountdownTimer />
            </div>
            <Claim label={t.claim} />
          </motion.div>

          <div className="flex flex-col gap-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.35 }}
              className="flex-1 border border-white/10 bg-surface p-8 sm:p-10"
              data-testid="offer-card-5"
            >
              <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-white/50">
                <Wallet size={15} className="text-gold" /> {t.o3_tag}
              </p>
              <p className="mt-5 font-display text-7xl font-bold leading-none gold-text">5%</p>
              <p className="mt-4 text-sm text-white/70">{t.o3_desc}</p>
              <Claim label={t.claim} />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.45 }}
              className="border border-gold/40 bg-gold/5 p-8"
              data-testid="offer-card-spin"
            >
              <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-gold">
                <Sparkles size={15} /> {t.o4_tag}
              </p>
              <p className="mt-3 text-sm text-white/70">{t.o4_desc}</p>
            </motion.div>
          </div>
        </div>

        <div className="mt-16 border border-white/10 bg-black/40 p-8">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">{t.terms_title}</p>
          <ul className="mt-4 space-y-2">
            {t.terms.map((x, i) => (
              <li key={i} className="text-sm text-white/50">• {x}</li>
            ))}
          </ul>
        </div>
      </main>

      <SiteFooter lang={lang} />
    </div>
  );
}
